import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Activity,
  DollarSign,
  Users,
  Wrench,
  AlertTriangle,
  RotateCw,
  Lock,
  ArrowUpRight,
  TrendingUp,
  Cpu
} from 'lucide-react';

const API_BASE = 'https://api.metamesh-uga.dev';

export function AdminStats() {
  const [adminKey, setAdminKey] = useState<string>(() => {
    return localStorage.getItem('admin_key') || '';
  });
  const [inputKey, setInputKey] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'agents' | 'tools' | 'transactions' | 'logs'>('overview');

  const { data: stats, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-stats', adminKey],
    queryFn: async () => {
      if (!adminKey) return null;
      const res = await fetch(`${API_BASE}/v1/admin/stats`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.status === 401) {
        localStorage.removeItem('admin_key');
        throw new Error('Invalid Admin Key');
      }
      if (!res.ok) throw new Error('Failed to load admin stats');
      return res.json();
    },
    enabled: !!adminKey,
    refetchInterval: 15000 // Autorefresh every 15s when unlocked
  });

  const { data: selfHealingData } = useQuery({
    queryKey: ['self-healing-history', adminKey],
    queryFn: async () => {
      if (!adminKey) return null;
      const res = await fetch(`${API_BASE}/v1/history`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (!res.ok) throw new Error('Failed to load history');
      return res.json();
    },
    enabled: !!adminKey
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputKey.trim()) {
      localStorage.setItem('admin_key', inputKey.trim());
      setAdminKey(inputKey.trim());
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_key');
    setAdminKey('');
    setInputKey('');
  };

  // 1. Password Entry / Lock Screen
  if (!adminKey || error?.message === 'Invalid Admin Key') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-4">
        <div className="w-full max-w-md p-8 glass rounded-3xl border border-slate-800/80 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 to-violet-500/5 pointer-events-none" />
          <div className="flex flex-col items-center text-center">
            <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/30 text-blue-400 mb-6">
              <Lock className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white">Admin Console</h1>
            <p className="text-sm text-slate-400 mt-2 mb-8">
              Authentication required to access operator analytics & billing logs.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                Secret Operator Key
              </label>
              <input
                type="password"
                value={inputKey}
                onChange={(e) => setInputKey(e.target.value)}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-850 hover:border-slate-750 focus:border-blue-500 focus:outline-none text-white text-center transition-all placeholder:text-slate-600"
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 text-center font-medium bg-red-500/5 border border-red-500/15 py-2 rounded-lg">
                ❌ {error.message}
              </p>
            )}
            <button
              type="submit"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 font-semibold text-white shadow-lg transition-all"
            >
              Sign In to Console
            </button>
          </form>
        </div>
      </div>
    );
  }

  // 2. Loading State
  if (isLoading && !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="relative flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
          <Shield className="w-5 h-5 text-blue-400 absolute animate-pulse" />
        </div>
        <p className="text-sm text-slate-400 mt-4">Retrieving operator diagnostics...</p>
      </div>
    );
  }

  // 3. Stats Unlocked
  const { overview, recent_calls, top_tools, top_agents, recent_transactions, recent_errors, usage_by_day, state_breakdown } = stats || {};
  const selfHealing = selfHealingData?.history || [];

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-800/50 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            <span className="text-xs font-semibold text-blue-400 uppercase tracking-widest">MetaMesh-UGA Operator Console</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-white mt-1">Management Hub</h1>
          <p className="text-sm text-slate-400 mt-1">Real-time health, billing flows and analytics</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-300 bg-slate-800/80 hover:bg-slate-700/80 rounded-xl border border-slate-700/40 transition-all disabled:opacity-50"
          >
            <RotateCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-xs font-semibold text-red-400 bg-red-500/5 hover:bg-red-500/10 rounded-xl border border-red-500/20 transition-all"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Earnings */}
        <div className="p-6 glass rounded-2xl border border-slate-800/60 flex items-center gap-5 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Earnings</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              ${(overview?.total_earnings_topups || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </h3>
            <p className="text-[10px] text-emerald-400 mt-1 flex items-center gap-1 font-medium">
              <ArrowUpRight className="w-3 h-3" /> Agent Spent: ${(overview?.total_agent_spending || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Total Calls */}
        <div className="p-6 glass rounded-2xl border border-slate-800/60 flex items-center gap-5 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Total Calls</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              {(overview?.total_calls || 0).toLocaleString()}
            </h3>
            <p className="text-[10px] text-blue-400 mt-1 flex items-center gap-1 font-medium">
              <TrendingUp className="w-3 h-3" /> Success: {overview?.success_rate}%
            </p>
          </div>
        </div>

        {/* Active Agents */}
        <div className="p-6 glass rounded-2xl border border-slate-800/60 flex items-center gap-5 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-fuchsia-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center text-violet-400">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Registered Agents</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              {(overview?.total_agents || 0).toLocaleString()}
            </h3>
            <p className="text-[10px] text-violet-400 mt-1 font-medium">
              Managed identities
            </p>
          </div>
        </div>

        {/* Catalog Tools */}
        <div className="p-6 glass rounded-2xl border border-slate-800/60 flex items-center gap-5 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Active Tools</p>
            <h3 className="text-2xl font-bold text-white mt-1">
              {overview?.active_tools} / {overview?.total_tools}
            </h3>
            <p className="text-[10px] text-amber-400 mt-1 font-medium">
              Latency: {overview?.avg_latency_ms} ms avg
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800/60 overflow-x-auto scrollbar-none gap-2">
        {(['overview', 'agents', 'tools', 'transactions', 'logs'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 capitalize transition-all whitespace-nowrap ${
              activeTab === tab
                ? 'border-blue-500 text-white bg-blue-500/5'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {tab === 'logs' ? 'Logs & Errors' : tab}
          </button>
        ))}
      </div>

      {/* TAB CONTENT: Overview */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
          {/* Main Chart Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                  Traffic Profile (Last 7 Days)
                </h3>
              </div>
              <div className="space-y-3">
                {usage_by_day && usage_by_day.length > 0 ? (
                  usage_by_day.map((d: any) => {
                    const totalCalls = d.calls || 0;
                    const errorsCount = d.errors || 0;
                    const errorPct = totalCalls > 0 ? (errorsCount / totalCalls) * 100 : 0;
                    return (
                      <div key={d.date} className="space-y-1">
                        <div className="flex justify-between text-xs text-slate-400">
                          <span>{d.date}</span>
                          <span className="font-semibold text-slate-200">
                            {totalCalls} calls {errorsCount > 0 && <span className="text-red-400">({errorsCount} errs)</span>}
                          </span>
                        </div>
                        <div className="relative w-full h-3 rounded-full bg-slate-900 overflow-hidden flex">
                          <div
                            style={{ width: `${100 - errorPct}%` }}
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
                          />
                          <div
                            style={{ width: `${errorPct}%` }}
                            className="h-full bg-red-500"
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-slate-500 text-center py-6">No usage data logged in the last 7 days.</p>
                )}
              </div>
            </div>

            {/* Self-healing history logs */}
            <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Cpu className="w-4 h-4 text-violet-400" />
                Registry Self-Healing Diagnostics
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                      <th className="py-2.5">Tool</th>
                      <th className="py-2.5">Transition</th>
                      <th className="py-2.5">Reason</th>
                      <th className="py-2.5">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selfHealing.length > 0 ? (
                      selfHealing.map((h: any, idx: number) => (
                        <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/20 text-slate-300">
                          <td className="py-3 font-semibold text-slate-100">{h.tool}</td>
                          <td className="py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-500/10 border border-violet-500/20 text-violet-300">
                              {h.action}
                            </span>
                          </td>
                          <td className="py-3 text-slate-400 max-w-xs truncate">{h.reason}</td>
                          <td className="py-3 text-slate-500">{new Date(h.timestamp).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-6 text-slate-500 text-center">No self-healing recovery actions logged yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right sidebar: Breakdown widgets */}
          <div className="space-y-6">
            {/* Top requested tools */}
            <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider text-slate-400">Top Rated Tools</h3>
              <div className="space-y-3">
                {top_tools && top_tools.length > 0 ? (
                  top_tools.map((t: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center text-xs">
                      <span className="font-semibold text-slate-200">{t.tool_name}</span>
                      <span className="px-2.5 py-0.5 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-300 font-bold">
                        {t.calls} calls
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-500 py-2">No tools executed yet.</p>
                )}
              </div>
            </div>

            {/* Catalog status breakdown */}
            <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider text-slate-400">Registry Distribution</h3>
              <div className="grid grid-cols-2 gap-4">
                {state_breakdown && state_breakdown.length > 0 ? (
                  state_breakdown.map((s: any, idx: number) => (
                    <div key={idx} className="p-3 bg-slate-900/60 border border-slate-850 rounded-xl">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{s.state}</span>
                      <h4 className="text-xl font-bold text-white mt-1">{s.count}</h4>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-slate-500 py-2 col-span-2">No registry data loaded.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Agents */}
      {activeTab === 'agents' && (
        <div className="glass rounded-2xl border border-slate-800/60 p-6 space-y-4 animate-fade-in">
          <h3 className="text-lg font-bold text-white">Registered Agents Directory</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                  <th className="py-2.5">Agent Details</th>
                  <th className="py-2.5">Wallet Address</th>
                  <th className="py-2.5">Prepaid Balance</th>
                  <th className="py-2.5">Total Spent</th>
                  <th className="py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {top_agents && top_agents.length > 0 ? (
                  top_agents.map((a: any, idx: number) => (
                    <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/20 text-slate-300">
                      <td className="py-4">
                        <div className="font-semibold text-slate-100">{a.agent_name || 'Unnamed Agent'}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{a.agent_id}</div>
                      </td>
                      <td className="py-4 font-mono text-slate-400">
                        {a.wallet_address || '0x...'}
                      </td>
                      <td className="py-4 font-semibold text-emerald-400">
                        ${(a.balance_usd || 0).toFixed(2)}
                      </td>
                      <td className="py-4 font-medium text-slate-200">
                        ${(a.total_spent_usd || 0).toFixed(2)}
                      </td>
                      <td className="py-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          a.suspended
                            ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                            : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                        }`}>
                          {a.suspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-slate-500 text-center">No agents registered or calling the gateway yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Tools */}
      {activeTab === 'tools' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Top Tools details */}
          <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
            <h3 className="text-lg font-bold text-white">Call Volume by Tool</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="py-2.5">Tool Name</th>
                    <th className="py-2.5 text-right">Call Count</th>
                  </tr>
                </thead>
                <tbody>
                  {top_tools && top_tools.length > 0 ? (
                    top_tools.map((t: any, idx: number) => (
                      <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/20 text-slate-300">
                        <td className="py-3 font-semibold text-slate-100">{t.tool_name}</td>
                        <td className="py-3 text-right font-bold text-blue-400">{t.calls} calls</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="py-6 text-slate-500 text-center">No tools records generated yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Active stats breakdown */}
          <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
            <h3 className="text-lg font-bold text-white">Tool Compliance Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Total Cataloged Tools</span>
                <span className="font-semibold text-slate-200">{overview?.total_tools}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Active (state = ACTIVE)</span>
                <span className="font-semibold text-emerald-400">{overview?.active_tools}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Average Execution Latency</span>
                <span className="font-semibold text-amber-400">{overview?.avg_latency_ms} ms</span>
              </div>
              <div className="h-0.5 bg-slate-800/60" />
              <p className="text-xs text-slate-500">
                Registry scans occur periodically via discovery triggers. Deprecated or non-functioning tools are self-healed into alternative routing targets automatically.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Transactions */}
      {activeTab === 'transactions' && (
        <div className="glass rounded-2xl border border-slate-800/60 p-6 space-y-4 animate-fade-in">
          <h3 className="text-lg font-bold text-white">Transactional Ledgers</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                  <th className="py-2.5">Transaction ID</th>
                  <th className="py-2.5">Agent</th>
                  <th className="py-2.5">Flow Type</th>
                  <th className="py-2.5">Value</th>
                  <th className="py-2.5">State</th>
                  <th className="py-2.5">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {recent_transactions && recent_transactions.length > 0 ? (
                  recent_transactions.map((t: any, idx: number) => (
                    <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/20 text-slate-300">
                      <td className="py-4 font-mono text-slate-400">TX_{t.id}</td>
                      <td className="py-4">
                        <div className="font-semibold text-slate-100">{t.agent_name || 'Unnamed Agent'}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">ID: {t.agent_id}</div>
                      </td>
                      <td className="py-4 uppercase text-slate-400 font-medium">{t.type}</td>
                      <td className={`py-4 font-bold ${
                        t.type === 'topup' ? 'text-emerald-400' : 'text-slate-300'
                      }`}>
                        {t.type === 'topup' ? '+' : '-'}${Number(t.amount).toFixed(3)}
                      </td>
                      <td className="py-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          t.status === 'completed'
                            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                            : t.status === 'failed'
                            ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                            : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                      <td className="py-4 text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-slate-500 text-center">No transactions logged in system ledgers.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB CONTENT: Logs */}
      {activeTab === 'logs' && (
        <div className="space-y-6 animate-fade-in">
          {/* System Error Trace */}
          <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
              Runtime Error Trace
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="py-2.5">Target Tool</th>
                    <th className="py-2.5">Error Message</th>
                    <th className="py-2.5">Reported Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_errors && recent_errors.length > 0 ? (
                    recent_errors.map((e: any, idx: number) => (
                      <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/10 text-red-200">
                        <td className="py-3.5 font-semibold text-slate-100">{e.tool_name}</td>
                        <td className="py-3.5 font-mono text-xs max-w-xl truncate text-red-400">{e.error_message}</td>
                        <td className="py-3.5 text-slate-500">{new Date(e.called_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="py-6 text-slate-500 text-center">No system faults reported recently!</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Real-time traffic stream */}
          <div className="p-6 glass rounded-2xl border border-slate-800/60 space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" />
              Real-time Traffic Stream
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="py-2.5">Call ID</th>
                    <th className="py-2.5">Caller identity</th>
                    <th className="py-2.5">Requested Tool</th>
                    <th className="py-2.5">Latency</th>
                    <th className="py-2.5">Status</th>
                    <th className="py-2.5">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_calls && recent_calls.length > 0 ? (
                    recent_calls.map((c: any, idx: number) => (
                      <tr key={idx} className="border-b border-slate-850 hover:bg-slate-800/20 text-slate-300">
                        <td className="py-3 text-slate-500">#{c.id}</td>
                        <td className="py-3 font-semibold text-slate-100">{c.agent_name || 'Anonymous client'}</td>
                        <td className="py-3 font-mono text-slate-400">{c.tool_name}</td>
                        <td className="py-3 text-slate-400">{c.latency_ms} ms</td>
                        <td className="py-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            c.status === 'success'
                              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                              : 'bg-red-500/10 border border-red-500/20 text-red-400'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="py-3 text-slate-500">{new Date(c.called_at).toLocaleString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 text-slate-500 text-center">No traffic logged through the gateway yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
